import React, { useState } from 'react';
import { Collapse, Button, Row, Col } from 'antd';
import {
  PlusOutlined,
  MinusOutlined,
  QuestionCircleOutlined,
  MessageOutlined,
  PhoneOutlined,
  MailOutlined
} from '@ant-design/icons';

const { Panel } = Collapse;

function AppFAQ() {
  const [activeKeys, setActiveKeys] = useState([]);

  const faqs = [
    {
      key: '1',
      question: "What makes OkBrahmin suitable for the Brahmin community?",
      answer: "Our platform is specifically designed with Brahmin cultural values in mind. We include subcaste filters, gothram matching, horoscope compatibility tools, and cultural preference settings to ensure a culturally sensitive and relevant matrimonial experience.",
      category: "Platform"
    },
    {
      key: '2',
      question: "How do I create a profile on OkBrahmin?",
      answer: "Creating your profile is simple and free. Sign in using your Google Account, complete your personal details including family background, subcaste, and preferences. Add photos and describe what you're looking for in a life partner.",
      category: "Getting Started"
    },
    {
      key: '3',
      question: "Can I search by specific subcaste preferences?",
      answer: "Absolutely! Our advanced search filters allow you to specify subcaste preferences, gothram, nakshatram, and other cultural criteria to find matches that align with your family traditions and community background.",
      category: "Search"
    },
    {
      key: '4',
      question: "Is horoscope matching available?",
      answer: "Yes, we provide integrated horoscope matching tools including raasi compatibility, nakshatram alignment, and dosham analysis. This feature is optional and can be used based on your personal preferences and family traditions.",
      category: "Features"
    },
    {
      key: '5',
      question: "Can I filter matches by education and profession?",
      answer: "Yes, our platform allows detailed filtering by educational qualifications, professional background, income range, and career aspirations to help you find compatible matches.",
      category: "Search"
    },
    {
      key: '6',
      question: "How do I connect with potential matches?",
      answer: "You can express interest in profiles, send personalized messages, and use our secure chat system. We encourage meaningful conversations to help you get to know potential matches before taking the next step.",
      category: "Communication"
    },
    {
      key: '7',
      question: "Are there community events organized?",
      answer: "We regularly share information about Brahmin community events, cultural celebrations, and matrimonial meets in our community section to help members connect beyond the digital platform.",
      category: "Community"
    },
    {
      key: '8',
      question: "Is the platform mobile-friendly?",
      answer: "Yes, OkBrahmin is fully responsive and works seamlessly on smartphones, tablets, and desktop computers. You can access all features from any device with an internet connection.",
      category: "Technical"
    },
    {
      key: '9',
      question: "Are there cultural resources available?",
      answer: "We have a dedicated section with articles on Brahmin traditions, marriage customs, astrological insights, and cultural practices to help enrich your understanding of our community heritage.",
      category: "Resources"
    },
    {
      key: '10',
      question: "What customer support is available?",
      answer: "Our support team understands the unique needs of the Brahmin community. Contact us through our help center, email, or phone for assistance with your matrimonial journey.",
      category: "Support"
    },
    {
      key: '11',
      question: "How is my privacy protected?",
      answer: "We employ enterprise-grade security measures to protect your personal information. You have full control over privacy settings and can choose what information to share and with whom.",
      category: "Privacy"
    }
  ];

  const categories = [...new Set(faqs.map(faq => faq.category))];

  const handleCollapseChange = (keys) => {
    setActiveKeys(keys);
  };

  return (
    <div style={{ 
      background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)', 
      padding: '100px 0' 
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        
        {/* Header Section */}
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            padding: '8px 20px',
            borderRadius: '25px',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '24px'
          }}>
            <QuestionCircleOutlined style={{ marginRight: '8px' }} />
            FAQ Section
          </div>
          
          <h2 style={{
            fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '24px',
            lineHeight: '1.2'
          }}>
            Frequently Asked <span style={{ color: '#667eea' }}>Questions</span>
          </h2>
          
          <p style={{
            fontSize: '1.25rem',
            color: '#6b7280',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.7'
          }}>
            Find answers to common questions about our platform, features, and how to make the most of your matrimonial journey.
          </p>
        </div>

        {/* FAQ Categories Filter */}
        <div style={{ 
          marginBottom: '60px', 
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '12px'
          }}>
            {categories.map(category => (
              <div key={category} style={{
                background: 'white',
                border: '2px solid #e5e7eb',
                borderRadius: '25px',
                padding: '8px 20px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#667eea',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#667eea';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.borderColor = '#667eea';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#667eea';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
              >
                {category}
              </div>
            ))}
          </div>
        </div>

        {/* Main FAQ Content */}
        <Row gutter={[48, 48]}>
          {/* FAQ Accordion */}
          <Col xs={24} lg={16}>
            <div style={{
              background: 'white',
              borderRadius: '24px',
              padding: '40px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)'
            }}>
              <Collapse
                ghost
                activeKey={activeKeys}
                onChange={handleCollapseChange}
                expandIcon={({ isActive }) => (
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: isActive ? '#667eea' : '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease'
                  }}>
                    {isActive ? 
                      <MinusOutlined style={{ color: 'white', fontSize: '14px' }} /> : 
                      <PlusOutlined style={{ color: '#6b7280', fontSize: '14px' }} />
                    }
                  </div>
                )}
                expandIconPosition="end"
              >
                {faqs.map((faq) => (
                  <Panel
                    key={faq.key}
                    header={
                      <div style={{ paddingRight: '20px' }}>
                        <div style={{
                          fontSize: '12px',
                          color: '#667eea',
                          fontWeight: '600',
                          marginBottom: '8px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {faq.category}
                        </div>
                        <h4 style={{
                          fontSize: '1.1rem',
                          fontWeight: '600',
                          color: '#1f2937',
                          margin: 0,
                          lineHeight: '1.4'
                        }}>
                          {faq.question}
                        </h4>
                      </div>
                    }
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid #e5e7eb',
                      marginBottom: '16px',
                      borderRadius: 0
                    }}
                  >
                    <div style={{ paddingTop: '20px', paddingRight: '50px' }}>
                      <p style={{
                        color: '#6b7280',
                        lineHeight: '1.7',
                        margin: 0,
                        fontSize: '16px'
                      }}>
                        {faq.answer}
                      </p>
                    </div>
                  </Panel>
                ))}
              </Collapse>
            </div>
          </Col>

          {/* Support Sidebar */}
          <Col xs={24} lg={8}>
            <div style={{ position: 'sticky', top: '100px' }}>
              {/* Quick Help Card */}
              <div style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                borderRadius: '24px',
                padding: '40px 30px',
                color: 'white',
                marginBottom: '30px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '24px'
                }}>
                  💬
                </div>
                
                <h3 style={{ color: 'white', fontSize: '1.5rem', marginBottom: '16px' }}>
                  Need More Help?
                </h3>
                
                <p style={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  marginBottom: '30px',
                  lineHeight: '1.6'
                }}>
                  Our support team is here to assist you with any questions about your matrimonial journey.
                </p>

                <Button
                  size="large"
                  style={{
                    background: 'white',
                    borderColor: 'white',
                    color: '#667eea',
                    borderRadius: '25px',
                    fontWeight: 'bold',
                    height: '50px',
                    padding: '0 30px',
                    width: '100%'
                  }}
                >
                  Contact Support
                </Button>
              </div>

              {/* Contact Options */}
              <div style={{
                background: 'white',
                borderRadius: '20px',
                padding: '30px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)'
              }}>
                <h4 style={{
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  color: '#1f2937',
                  marginBottom: '24px'
                }}>
                  Get in Touch
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                  >
                    <MessageOutlined style={{ color: '#667eea', fontSize: '18px', marginRight: '12px' }} />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>Live Chat</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Available 24/7</div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                  >
                    <MailOutlined style={{ color: '#667eea', fontSize: '18px', marginRight: '12px' }} />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>Email Support</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>support@okbrahmin.com</div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                  >
                    <PhoneOutlined style={{ color: '#667eea', fontSize: '18px', marginRight: '12px' }} />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>Phone Support</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Mon-Fri 9AM-6PM IST</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

      </div>
    </div>
  );
}

export default AppFAQ;